// timeBus.ts — cross-engine time-pin bus.
//
// Per APPLICATION_PATH/design/20-design/interaction-designer/state-transitions.md
// (4D timeline state machine): every engine consumes the same scrub time
// through this bus. The host also subscribes to render the timeline-pin
// chip (`gv-timeline-pinned-tick`) and the per-engine ack window check
// (RFC-102 P8 — engines ack within 50 ms or surface "out-of-sync" warning).

import { useEffect, useState } from 'react';

export type TimelineMode = 'live' | 'paused' | 'replaying' | 'simulated' | 'scheduled' | 'pinned' | 'compare';

export interface TimePin {
  /** ISO-8601 string (UTC). */
  iso: string;
  /** Epoch ms. */
  ts: number;
}

export interface TimeBusState {
  current: TimePin;
  mode: TimelineMode;
  /** Active scenario id when mode is 'compare' or 'simulated'. */
  scenarioId?: string;
}

type Listener = (state: TimeBusState) => void;

class TimeBus {
  private listeners = new Set<Listener>();
  private state: TimeBusState = {
    current: { iso: new Date().toISOString(), ts: Date.now() },
    mode: 'live',
  };
  /** Per-engine ack timestamps (engine id → epoch ms). */
  private acks = new Map<string, number>();

  publish(next: Partial<TimeBusState>): void {
    this.state = { ...this.state, ...next };
    for (const fn of this.listeners) fn(this.state);
  }

  /** Engines call this after they've successfully applied the time. */
  ack(engineId: string): void {
    this.acks.set(engineId, Date.now());
  }

  /** Returns true if any engine has not acked the current state within `windowMs`. */
  isStale(windowMs: number = 50): boolean {
    const now = Date.now();
    const stateTs = this.state.current.ts;
    for (const [, ackTs] of this.acks) {
      if (ackTs < stateTs && now - stateTs > windowMs) return true;
    }
    return false;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => {
      this.listeners.delete(fn);
    };
  }

  getSnapshot(): TimeBusState {
    return this.state;
  }

  reset(): void {
    this.listeners.clear();
    this.acks.clear();
    this.state = {
      current: { iso: new Date().toISOString(), ts: Date.now() },
      mode: 'live',
    };
  }
}

export const timeBus = new TimeBus();

export function useTime(): TimeBusState {
  const [snap, setSnap] = useState<TimeBusState>(() => timeBus.getSnapshot());
  useEffect(() => timeBus.subscribe(setSnap), []);
  return snap;
}
